/**
 * Generic column-state hook for data tables.
 *
 * Owns three pieces of UI state, persisted to localStorage:
 *   - visibility: which columns are shown
 *   - order:      drag-to-reorder via the Columns dropdown
 *   - widths:     drag-to-resize via the column header handle
 *
 * Replaces three near-identical copies that used to live in:
 *   - features/accounts/hooks/useAccountsColumnState.ts (accounts list)
 *   - features/accounts/components/SensorsTab.tsx (inline)
 *   - features/accounts/components/TransmittersTab.tsx (inline)
 *
 * The hook is intentionally agnostic to the column shape — it only needs a
 * `key` (string) and a `defaultVisible` flag. Per-feature ColumnDef types
 * extend this minimum and stay where they are.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

export interface ColumnLike {
  key: string;
  defaultVisible?: boolean;
}

export interface ColumnStateStorageKeys {
  visibility: string;
  order: string;
  widths: string;
}

export interface UseColumnStateOptions<C extends ColumnLike> {
  /** Full column registry (every column the table can possibly render). */
  columns: C[];
  /** localStorage keys for the three pieces of state. */
  storageKeys: ColumnStateStorageKeys;
}

export interface ColumnState<C extends ColumnLike> {
  /** All columns in user-chosen render order (visible + hidden). */
  orderedColumns: C[];
  /** Only the columns currently visible, in render order. */
  visibleColumns: C[];
  columnOrder: string[];
  columnVisibility: Record<string, boolean>;
  columnWidths: Record<string, number>;
  isVisible: (key: string) => boolean;
  toggleColumn: (key: string) => void;
  /** Bulk replace the visibility map — used by view presets etc. */
  setColumnVisibility: (next: Record<string, boolean>) => void;
  setColumnOrder: (next: string[]) => void;
  setColumnWidth: (key: string, width: number) => void;
}

function readJson<T>(storageKey: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) return JSON.parse(stored) as T;
  } catch {
    // malformed — ignore
  }
  return fallback;
}

export function useColumnState<C extends ColumnLike>({
  columns,
  storageKeys,
}: UseColumnStateOptions<C>): ColumnState<C> {
  const defaultVisibility = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const col of columns) out[col.key] = col.defaultVisible !== false;
    return out;
  }, [columns]);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => ({
    ...defaultVisibility,
    ...readJson<Record<string, boolean>>(storageKeys.visibility, {}),
  }));

  const [columnOrder, setColumnOrderState] = useState<string[]>(() => {
    const stored = readJson<string[]>(storageKeys.order, []);
    if (!stored.length) return columns.map((c) => c.key);
    // Drop unknown keys (columns removed from the schema), append new ones at the end.
    const known = new Set(columns.map((c) => c.key));
    const kept = stored.filter((k) => known.has(k));
    const missing = columns.map((c) => c.key).filter((k) => !kept.includes(k));
    return [...kept, ...missing];
  });

  const [columnWidths, setColumnWidthsState] = useState<Record<string, number>>(
    () => readJson<Record<string, number>>(storageKeys.widths, {}),
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKeys.visibility, JSON.stringify(columnVisibility));
    } catch { /* ignore */ }
  }, [columnVisibility, storageKeys.visibility]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKeys.order, JSON.stringify(columnOrder));
    } catch { /* ignore */ }
  }, [columnOrder, storageKeys.order]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKeys.widths, JSON.stringify(columnWidths));
    } catch { /* ignore */ }
  }, [columnWidths, storageKeys.widths]);

  const toggleColumn = useCallback((key: string) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  }, []);

  const setColumnOrder = useCallback((next: string[]) => {
    setColumnOrderState(next);
  }, []);

  const setColumnWidth = useCallback((key: string, width: number) => {
    setColumnWidthsState((prev) => ({ ...prev, [key]: width }));
  }, []);

  const isVisible = useCallback(
    (key: string) => columnVisibility[key] !== false,
    [columnVisibility],
  );

  const orderedColumns = useMemo<C[]>(() => {
    const byKey = new Map(columns.map((c) => [c.key, c] as const));
    return columnOrder
      .map((k) => byKey.get(k))
      .filter((c): c is C => Boolean(c));
  }, [columns, columnOrder]);

  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => isVisible(c.key)),
    [orderedColumns, isVisible],
  );

  return {
    orderedColumns,
    visibleColumns,
    columnOrder,
    columnVisibility,
    columnWidths,
    isVisible,
    toggleColumn,
    setColumnVisibility,
    setColumnOrder,
    setColumnWidth,
  };
}
